import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';

import { getView } from '../../engine/prism.js';
import { partAt, resolvePartPath, type ModelOp } from '../../lib/model-doc.js';
import { allPartPaths, setPartsFlag } from '../../lib/model-tree.js';
import {
  addOp,
  applyCount,
  collapseKey,
  duplicateOp,
  opDefaults,
  opFlag,
  opGroups,
  opLabel,
  readCollapsed,
  remapCollapsed,
  removeOp,
  reorderedIndex,
  reorderOp,
  setOpFlagAt,
  writeCollapsed,
} from '../../lib/op-stack.js';
import { logEditor } from '../../state/console.js';
import { useModel } from '../../state/model.js';
import { ColorField, IconButton, MenuButton, NumberField, PropRow, SelectField, Toggle, VecField, type MenuItem } from '../ui/controls.js';
import { Icon, type IconName } from '../ui/icons.js';
import { ObjectFields } from './PartInspector.js';
import './op-stack.css';

/**
 * The part's op stack as a Blender-style modifier panel: one collapsible card
 * per op with viewport / export toggles, Apply, duplicate, delete and a drag
 * handle; a grouped "Add modifier" menu; and a before/after toggle that shows
 * the part without its ops in the viewport (modelOp `setBypass`).
 *
 * Apply on card N bakes ops 1..N (modelOp `applyOps {count: N}`), asking
 * first when N > 1: the stack runs top to bottom, so an op further down can
 * only be baked together with everything above it (Blender applies a lower
 * modifier on its own and warns that the result may differ; baking the ops
 * above keeps what you see).
 */

/** Runs a `modelOp` command in the model viewport; failures go to the console. */
export async function modelOp(name: string, params: Record<string, unknown> = {}): Promise<boolean> {
  const view = getView('model');
  if (!view) {
    logEditor('warn', `${name}: open the model viewport first`);
    return false;
  }
  const outcome = await view.modelCommand({ op: 'modelOp', name, ...params });
  if (!outcome.ok && outcome.error) logEditor('warn', outcome.error);
  return outcome.ok;
}

const OP_ICON: Record<string, IconName> = {
  subsurf: 'sphere',
  subdivide: 'grid',
  smooth: 'smooth',
  bevel: 'cube',
  noise: 'mountain',
  twist: 'rotate',
  taper: 'arrowUp',
  bend: 'refresh',
  mirror: 'mirror',
  flatShade: 'flat',
  inflate: 'circle',
  boolean: 'layers',
  vertexColor: 'bucket',
  sculpt: 'pencil',
  fur: 'particles',
  remesh: 'wireframe',
  decimate: 'wireframe',
  unwrap: 'image',
};

/** Choices for enum fields of ops whose names ObjectFields would misread. */
const OP_ENUMS: Record<string, Record<string, readonly string[]>> = {
  unwrap: { method: ['smart', 'angle', 'conformal', 'cube', 'cylinder', 'sphere'] },
  remesh: { mode: ['voxel', 'quad'] },
  decimate: { mode: ['collapse', 'planar', 'unsubdivide'] },
};

const ALWAYS_SKIP = ['op', 'viewport', 'export'];
const AXES = ['x', 'y', 'z'];

export function OpStack({ ops, partPath, onChange }: { ops: ModelOp[]; partPath: string; onChange: (ops: ModelOp[]) => void }) {
  const modelPath = useModel((s) => s.path);
  const [collapsed, setCollapsedState] = useState(readCollapsed);
  const [bypass, setBypass] = useState(false);
  const [armed, setArmed] = useState<number | null>(null);
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [gap, setGap] = useState<number | null>(null);
  const pressedAt = useRef(0);

  // Before / after: the viewport shows this part without its ops while on.
  useEffect(() => setBypass(false), [partPath]);
  useEffect(() => {
    if (!bypass) return;
    modelOp('setBypass', { paths: [partPath] });
    return () => {
      modelOp('setBypass', { paths: [] });
    };
  }, [bypass, partPath]);

  const setCollapsed = (next: Set<string>) => {
    setCollapsedState(next);
    writeCollapsed(next);
  };
  const isCollapsed = (i: number) => collapsed.has(collapseKey(modelPath, partPath, i));
  const toggleCollapsed = (i: number) => {
    const key = collapseKey(modelPath, partPath, i);
    const next = new Set(collapsed);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setCollapsed(next);
  };
  /** Change the ops and carry each card's collapse state to its new index. */
  const change = (next: ModelOp[], map?: (i: number) => number | null) => {
    if (map) setCollapsed(remapCollapsed(collapsed, modelPath, partPath, ops.length, map));
    onChange(next);
  };

  const addItems = (): MenuItem[] =>
    opGroups().flatMap((group) => [
      { id: `h-${group.label}`, label: group.label, heading: true },
      ...group.ops.map((op) => ({ id: op, label: opLabel(op), icon: OP_ICON[op] ?? 'wand', run: () => change(addOp(ops, op)) })),
    ]);

  const apply = (i: number) => {
    const count = applyCount(i);
    if (count > 1) {
      const names = ops.slice(0, count).map((op, j) => `${j + 1}. ${opLabel(op.op)}${opFlag(op, 'export') ? '' : ' (export off: dropped)'}`);
      if (!window.confirm(`Apply ${count} ops into the part's mesh?\n\n${names.join('\n')}\n\nThe stack runs top to bottom, so the ops above this one are applied with it.`)) return;
    }
    modelOp('applyOps', { path: partPath, count });
  };

  function onDragOver(event: DragEvent<HTMLElement>, i: number) {
    if (dragFrom === null) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    const r = event.currentTarget.getBoundingClientRect();
    const g = event.clientY - r.top < r.height / 2 ? i : i + 1;
    if (g !== gap) setGap(g);
  }
  function onDrop(event: DragEvent<HTMLElement>) {
    event.preventDefault();
    const from = dragFrom;
    const to = gap;
    setDragFrom(null);
    setGap(null);
    setArmed(null);
    if (from === null || to === null) return;
    const next = reorderOp(ops, from, to);
    if (next.every((op, j) => op === ops[j])) return;
    change(next, (i) => reorderedIndex(i, from, to));
  }

  return (
    <section className="card is-open op-stack-card">
      <header className="card-head">
        <span className="card-toggle op-stack-title">
          <Icon name="wand" size={14} className="card-icon" />
          <span className="card-title">Modifiers</span>
          {ops.length > 0 && <span className="card-sub">{ops.length}</span>}
        </span>
        <div className="card-actions">
          <button
            type="button"
            className={`ibtn${bypass ? ' is-active' : ''}`}
            disabled={ops.length === 0}
            aria-pressed={bypass}
            title={bypass ? 'Showing the part without its ops (click to show the result)' : 'Before / after: show the part without its ops (click to toggle, or press and hold)'}
            onPointerDown={() => {
              pressedAt.current = performance.now();
              setBypass((b) => !b);
            }}
            onPointerUp={() => {
              // Held down: momentary preview, released → back.
              if (performance.now() - pressedAt.current > 350) setBypass((b) => !b);
            }}
            onClick={(e) => {
              // Keyboard activation (Enter / Space) has no pointer events.
              if (e.detail === 0) setBypass((b) => !b);
            }}
          >
            <Icon name={bypass ? 'eyeOff' : 'onion'} size={14} />
          </button>
          <MenuButton className="ibtn" title="Add modifier" items={addItems}>
            <Icon name="plus" size={14} />
          </MenuButton>
        </div>
      </header>
      <div className="card-body">
        {ops.length === 0 && <p className="muted card-note">No modifiers. Ops run top to bottom on the part's mesh; add one with +.</p>}
        <ol className="op-stack" onDragLeave={(e) => e.target === e.currentTarget && setGap(null)}>
          {ops.map((op, i) => {
            const open = !isCollapsed(i);
            const viewport = opFlag(op, 'viewport');
            const exported = opFlag(op, 'export');
            const indicator = dragFrom !== null && gap === i ? ' drop-before' : dragFrom !== null && gap === i + 1 && i === ops.length - 1 ? ' drop-after' : '';
            return (
              <li
                key={i}
                className={`op-card${open ? ' is-open' : ''}${viewport ? '' : ' is-off'}${dragFrom === i ? ' is-dragging' : ''}${indicator}`}
                draggable={armed === i}
                onDragStart={(e) => {
                  setDragFrom(i);
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('application/x-prism-op', String(i));
                }}
                onDragEnd={() => {
                  setDragFrom(null);
                  setGap(null);
                  setArmed(null);
                }}
                onDragOver={(e) => onDragOver(e, i)}
                onDrop={onDrop}
              >
                <div className="op-card-head">
                  <span
                    className="op-grip"
                    title="Drag to reorder"
                    onPointerDown={() => setArmed(i)}
                    onPointerUp={() => setArmed(null)}
                  >
                    <svg width="10" height="14" viewBox="0 0 10 14" aria-hidden="true">
                      {[2, 7, 12].map((y) => (
                        <g key={y}>
                          <circle cx="3" cy={y} r="1.2" fill="currentColor" />
                          <circle cx="7" cy={y} r="1.2" fill="currentColor" />
                        </g>
                      ))}
                    </svg>
                  </span>
                  <button type="button" className="op-card-toggle" aria-expanded={open} onClick={() => toggleCollapsed(i)}>
                    <Icon name={open ? 'chevronDown' : 'chevronRight'} size={12} />
                    <Icon name={OP_ICON[op.op] ?? 'wand'} size={13} className="op-card-icon" />
                    <span className="op-card-name">{opLabel(op.op)}</span>
                  </button>
                  <IconButton
                    icon={viewport ? 'eye' : 'eyeOff'}
                    size={13}
                    className={viewport ? 'op-flag is-on' : 'op-flag'}
                    title={viewport ? 'Shown in the viewport (click to hide)' : 'Hidden in the viewport (click to show)'}
                    onClick={() => change(setOpFlagAt(ops, i, 'viewport', !viewport))}
                  />
                  <IconButton
                    icon="camera"
                    size={13}
                    className={exported ? 'op-flag is-on' : 'op-flag is-crossed'}
                    title={exported ? 'Used on export and in game (click to skip)' : 'Skipped on export and in game (click to use)'}
                    onClick={() => change(setOpFlagAt(ops, i, 'export', !exported))}
                  />
                  <IconButton
                    icon="check"
                    size={13}
                    title={i === 0 ? 'Apply: bake into the part mesh' : `Apply: bake ops 1–${i + 1} into the part mesh`}
                    onClick={() => apply(i)}
                  />
                  <MenuButton
                    className="ibtn"
                    title="More"
                    items={[
                      { id: 'dup', label: 'Duplicate', icon: 'copy', run: () => change(duplicateOp(ops, i), (j) => (j <= i ? j : j + 1)) },
                      { id: 'first', label: 'Move to first', icon: 'arrowUp', disabled: i === 0, run: () => change(reorderOp(ops, i, 0), (j) => reorderedIndex(j, i, 0)) },
                      { id: 'up', label: 'Move up', icon: 'chevronUp', disabled: i === 0, run: () => change(reorderOp(ops, i, i - 1), (j) => reorderedIndex(j, i, i - 1)) },
                      { id: 'down', label: 'Move down', icon: 'chevronDown', disabled: i === ops.length - 1, run: () => change(reorderOp(ops, i, i + 2), (j) => reorderedIndex(j, i, i + 2)) },
                      {
                        id: 'last',
                        label: 'Move to last',
                        icon: 'arrowDown',
                        disabled: i === ops.length - 1,
                        run: () => change(reorderOp(ops, i, ops.length), (j) => reorderedIndex(j, i, ops.length)),
                      },
                      { id: 'apply', label: i === 0 ? 'Apply' : `Apply ops 1–${i + 1}`, icon: 'check', run: () => apply(i) },
                    ]}
                  >
                    <Icon name="more" size={13} />
                  </MenuButton>
                  <IconButton
                    icon="copy"
                    size={13}
                    title="Duplicate"
                    onClick={() => change(duplicateOp(ops, i), (j) => (j <= i ? j : j + 1))}
                  />
                  <IconButton
                    icon="x"
                    size={13}
                    title="Delete"
                    onClick={() => change(removeOp(ops, i), (j) => (j === i ? null : j > i ? j - 1 : j))}
                  />
                </div>
                {open && (
                  <div className="op-card-body">
                    <OpFields
                      op={op}
                      partPath={partPath}
                      onChange={(next) => {
                        const copy = ops.slice();
                        copy[i] = next;
                        onChange(copy);
                      }}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}

/** Sets or removes one field of an op. */
function withField(op: ModelOp, key: string, value: unknown): ModelOp {
  const next: ModelOp = { ...op };
  if (value === undefined) delete next[key];
  else next[key] = value;
  return next;
}

/** Parameters of one op with typed controls (no raw JSON for known fields). */
function OpFields({ op, partPath, onChange }: { op: ModelOp; partPath: string; onChange: (op: ModelOp) => void }) {
  const set = (key: string, value: unknown) => onChange(withField(op, key, value));
  const scalars = (skip: string[] = []) => (
    <ObjectFields
      value={op}
      defaults={opDefaults(op.op)}
      skip={[...ALWAYS_SKIP, ...skip, ...Object.keys(OP_ENUMS[op.op] ?? {})]}
      onChange={(next) => onChange({ ...(next as ModelOp), op: op.op })}
    />
  );
  const enums = Object.entries(OP_ENUMS[op.op] ?? {}).map(([key, options]) => (
    <PropRow key={key} label={key[0]!.toUpperCase() + key.slice(1)} hint={key}>
      <SelectField value={typeof op[key] === 'string' ? (op[key] as string) : (opDefaults(op.op)[key] as string | undefined)} options={options} onChange={(v) => set(key, v)} />
    </PropRow>
  ));

  switch (op.op) {
    case 'boolean':
      return (
        <>
          <BooleanTarget value={typeof op.with === 'string' ? op.with : ''} partPath={partPath} onChange={(v) => set('with', v)} />
          {scalars(['with'])}
        </>
      );
    case 'vertexColor':
      return <VertexColorFields op={op} onChange={onChange} />;
    case 'sculpt':
      return <SculptOpFields op={op} onChange={onChange} />;
    case 'fur':
      return <FurFields op={op} onChange={onChange} />;
    default:
      return (
        <>
          {enums}
          {scalars()}
        </>
      );
  }
}

/** Boolean `with`: pick another part of the model (not this one or its children). */
function BooleanTarget({ value, partPath, onChange }: { value: string; partPath: string; onChange: (v: string) => void }) {
  const doc = useModel((s) => s.doc);
  const options = useMemo(
    () => (doc ? allPartPaths(doc).filter((p) => p !== partPath && !p.startsWith(`${partPath}/`)) : []),
    [doc, partPath],
  );
  const target = doc && value ? resolvePartPath(doc, value) : null;
  const targetPart = doc && target ? partAt(doc, target) : null;
  return (
    <>
      <PropRow label="With" hint="the other part (by path)">
        <SelectField value={value || undefined} placeholder="Pick a part…" options={options} onChange={onChange} />
      </PropRow>
      {value && !targetPart && <p className="muted card-note op-warning">No part “{value}” in this model.</p>}
      {targetPart && (
        <PropRow label="Hide cutter" hint="hidden parts still cut; shown ones render too">
          <Toggle
            checked={Boolean(targetPart.hidden)}
            onChange={(on) => {
              const d = useModel.getState().doc;
              if (d) useModel.getState().update(setPartsFlag(d, [value], 'hidden', on));
            }}
          />
        </PropRow>
      )}
    </>
  );
}

type Rgba = number[];

/** vertexColor: one tint, or a gradient along an axis. */
function VertexColorFields({ op, onChange }: { op: ModelOp; onChange: (op: ModelOp) => void }) {
  const gradient = op.gradient && typeof op.gradient === 'object' ? (op.gradient as { from?: Rgba; to?: Rgba; axis?: string }) : null;
  const setGradient = (patch: Record<string, unknown>) => onChange({ ...op, gradient: { from: [0, 0, 0, 1], to: [1, 1, 1, 1], axis: 'y', ...gradient, ...patch } });
  return (
    <>
      <PropRow label="Mode">
        <SelectField
          value={gradient ? 'gradient' : 'color'}
          options={[
            { value: 'color', label: 'Color' },
            { value: 'gradient', label: 'Gradient' },
          ]}
          onChange={(mode) => {
            const next: ModelOp = { ...op };
            if (mode === 'gradient') {
              delete next.color;
              next.gradient = { from: [0, 0, 0, 1], to: Array.isArray(op.color) ? op.color : [1, 1, 1, 1], axis: 'y' };
            } else {
              delete next.gradient;
              next.color = gradient?.to ?? [1, 1, 1, 1];
            }
            onChange(next);
          }}
        />
      </PropRow>
      {gradient ? (
        <>
          <PropRow label="From" hint="color at the low end">
            <ColorField value={gradient.from ?? [0, 0, 0, 1]} onChange={(v) => setGradient({ from: v })} />
          </PropRow>
          <PropRow label="To" hint="color at the high end">
            <ColorField value={gradient.to ?? [1, 1, 1, 1]} onChange={(v) => setGradient({ to: v })} />
          </PropRow>
          <PropRow label="Axis">
            <SelectField value={gradient.axis ?? 'y'} options={AXES} onChange={(v) => setGradient({ axis: v })} />
          </PropRow>
        </>
      ) : (
        <PropRow label="Color">
          <ColorField value={Array.isArray(op.color) ? (op.color as Rgba) : [1, 1, 1, 1]} onChange={(v) => onChange({ ...op, color: v })} />
        </PropRow>
      )}
    </>
  );
}

/** sculpt op: the recorded strokes are edited in Sculpt mode; here only their count, a clear and symmetry. */
function SculptOpFields({ op, onChange }: { op: ModelOp; onChange: (op: ModelOp) => void }) {
  const strokes = Array.isArray(op.strokes) ? op.strokes.length : 0;
  return (
    <>
      <PropRow label="Strokes" hint="recorded brush strokes (edit them in Sculpt mode)">
        <div className="op-inline">
          <span className="mono">{strokes}</span>
          <button
            type="button"
            className="ghost small"
            disabled={strokes === 0}
            onClick={() => {
              if (window.confirm(`Clear all ${strokes} sculpt strokes of this op?`)) onChange({ ...op, strokes: [] });
            }}
          >
            <Icon name="trash" size={12} /> Clear
          </button>
          <button type="button" className="ghost small" title="Sculpt this part" onClick={() => useModel.getState().setSculpt(true)}>
            <Icon name="pencil" size={12} /> Sculpt
          </button>
        </div>
      </PropRow>
      <PropRow label="Symmetry" hint="strokes also act mirrored across this axis">
        <SelectField value={typeof op.symmetry === 'string' ? op.symmetry : 'none'} options={['none', ...AXES]} onChange={(v) => onChange(withField(op, 'symmetry', v === 'none' ? undefined : v))} />
      </PropRow>
    </>
  );
}

interface Ball {
  center?: number[];
  radius?: number;
}

/** fur op: material, scalars, optional comb direction / colors and bald spots. */
function FurFields({ op, onChange }: { op: ModelOp; onChange: (op: ModelOp) => void }) {
  const materials = useModel((s) => Object.keys(s.doc?.materials ?? {}).join('\n'));
  const names = materials ? materials.split('\n') : [];
  const material = typeof op.material === 'string' ? op.material : 'fur';
  const set = (key: string, value: unknown) => onChange(withField(op, key, value));
  const exclude = Array.isArray(op.exclude) ? (op.exclude as Ball[]) : [];
  const optional = (key: 'direction' | 'rootColor' | 'tipColor', label: string, hint: string, initial: number[]) => {
    const value = Array.isArray(op[key]) ? (op[key] as number[]) : null;
    return (
      <PropRow label={label} hint={hint}>
        <div className="op-inline">
          <Toggle checked={value !== null} onChange={(on) => set(key, on ? initial : undefined)} />
          {value &&
            (key === 'direction' ? (
              <VecField value={value} onChange={(v) => set(key, v)} />
            ) : (
              <ColorField value={value} alpha={false} onChange={(v) => set(key, v.slice(0, 3))} />
            ))}
        </div>
      </PropRow>
    );
  };
  return (
    <>
      <PropRow label="Material" hint="shell material: a fur texture with alpha mask, double sided">
        <SelectField value={material} options={names.includes(material) ? names : [...names, material]} onChange={(v) => set('material', v)} />
      </PropRow>
      {!names.includes(material) && <p className="muted card-note op-warning">No material “{material}” yet: add one with a fur texture in Materials.</p>}
      <ObjectFields
        value={op}
        defaults={opDefaults('fur')}
        skip={[...ALWAYS_SKIP, 'material', 'direction', 'rootColor', 'tipColor', 'exclude']}
        onChange={(next) => onChange({ ...(next as ModelOp), op: op.op })}
      />
      {optional('direction', 'Comb direction', 'part-space direction the fur lies towards', [0, 0, 1])}
      {optional('rootColor', 'Root color', 'vertex color at the roots', [0.2, 0.15, 0.1])}
      {optional('tipColor', 'Tip color', 'vertex color at the tips', [0.8, 0.7, 0.6])}
      <PropRow label="Bald spots" hint="fur fades to nothing inside these spheres">
        <button type="button" className="ghost small" onClick={() => set('exclude', [...exclude, { center: [0, 0, 0], radius: 0.05 }])}>
          <Icon name="plus" size={12} /> Add
        </button>
      </PropRow>
      {exclude.map((ball, j) => (
        <div key={j} className="op-ball">
          <VecField
            value={ball.center ?? [0, 0, 0]}
            onChange={(v) => set('exclude', exclude.map((b, k) => (k === j ? { ...b, center: v } : b)))}
          />
          <NumberField value={ball.radius ?? 0.05} step={0.01} min={0} title="Radius" onChange={(v) => set('exclude', exclude.map((b, k) => (k === j ? { ...b, radius: v } : b)))} />
          <IconButton icon="x" size={12} title="Remove" onClick={() => set('exclude', exclude.filter((_, k) => k !== j))} />
        </div>
      ))}
    </>
  );
}
